/**
 * RouteProgress — YouTube-style 2px top progress bar that animates
 * 0 → 90% on every route change and snaps to 100% when the new route
 * has painted (one rAF after location change). Pure Tailwind, no deps.
 *
 * Mounted once at the app root.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export const RouteProgress = () => {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const isFirstRender = useRef(true);
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    // Skip the very first mount — no perceived navigation occurred.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Clear any in-flight timers from a previous transition
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];

    setVisible(true);
    setProgress(15);

    // Ramp up to ~90% over ~400ms (logarithmic feel)
    timersRef.current.push(
      window.setTimeout(() => setProgress(45), 80),
      window.setTimeout(() => setProgress(70), 200),
      window.setTimeout(() => setProgress(88), 380),
    );

    // Snap to 100% one frame after route renders, then fade out
    const finishId = window.requestAnimationFrame(() => {
      timersRef.current.push(
        window.setTimeout(() => setProgress(100), 50),
        window.setTimeout(() => {
          setVisible(false);
          // reset for next nav after fade-out completes
          window.setTimeout(() => setProgress(0), 200);
        }, 250),
      );
    });

    return () => {
      window.cancelAnimationFrame(finishId);
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
  }, [location.pathname, location.search]);

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-0.5 z-[200] pointer-events-none overflow-hidden"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 200ms ease-out" }}
    >
      <div
        className="h-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.5)]"
        style={{
          width: `${progress}%`,
          transition: "width 200ms cubic-bezier(0.25, 0.1, 0.25, 1)",
        }}
      />
    </div>
  );
};

export default RouteProgress;
